import { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Col, DatePicker, Form, FormInstance, Input, Row, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import apiRoutes from 'config/apiRoute';
import { convertHrsMinsToMins, convertMinsToHrsMins } from 'utils/string.utils';
import SelectSearch from '../SelectSearch';
import UserAvatar from '../UserAvatar';
import styles from './Timer.module.less';

const { TextArea } = Input;
const { Text } = Typography;

// The time field behaves like a stopwatch keypad: every digit typed shifts the previous ones
// to the left (6 -> 00:06, 60 -> 00:60, 600 -> 06:00). The digits typed so far are kept in a
// ref instead of being re-read from the input, because the displayed value can be reformatted
// from the outside (the running stopwatch rewrites it) and re-parsing it would corrupt the
// next keystroke.
const MAX_TIME_DIGITS = 4;

const formatTimeDigits = (digits: string): string => {
  const padded = digits.padStart(4, '0');
  return `${padded.slice(0, -2)}:${padded.slice(-2)}`;
};

// Keeps at most HH:MM worth of digits, dropping anything typed past the limit instead of
// letting it shift the value around (99:59 is the ceiling).
const takeTimeDigits = (value?: string): string =>
  (value ?? '')
    .replace(/\D/g, '')
    .replace(/^0+/, '')
    .slice(0, MAX_TIME_DIGITS);

// Turns a partially typed value into a valid one: 00:60 -> 01:00, 00:90 -> 01:30.
const normalizeTime = (value?: string): string => {
  const minutes = convertHrsMinsToMins(value ?? '');
  return Number.isFinite(minutes) ? convertMinsToHrsMins(minutes) : '00:00';
};

const taskUsersCanSelectUser = [
  'christopher@savageglobalent.com',
  'santiago@savageglobalent.com',
  'marcelo@savageglobalent.com',
  'andrew@savageglobalent.com',
  'francisco@savageglobalent.com',
];
const requestUsersCanSelectUser = [
  'christopher@savageglobalent.com',
  'santiago@savageglobalent.com',
  'marcelo@savageglobalent.com',
  'andrew@savageglobalent.com',
  'francisco@savageglobalent.com',
];

interface TimerFormProps {
  form: FormInstance;
  projectId?: string;
  initialUser?: any;
  showUserSelection?: 'task' | 'request';
  requiredDescription?: boolean;
  onChangeTimer?: (value: string) => void;
  onTimeFocusChange?: (focused: boolean) => void;
}

const TimerForm: FC<TimerFormProps> = ({
  form,
  projectId,
  initialUser,
  showUserSelection,
  requiredDescription,
  onChangeTimer,
  onTimeFocusChange,
}) => {
  const user = useSelector((store: any) => store.auth.user);
  const timeDigitsRef = useRef('');

  const applyTimeDigits = (digits: string) => {
    const nextDigits = takeTimeDigits(digits);
    timeDigitsRef.current = nextDigits;

    const formatted = formatTimeDigits(nextDigits);

    form.setFieldsValue({ time: formatted });
    onChangeTimer?.(formatted);
  };

  const wrappedUser = initialUser ? { ...initialUser, user: initialUser.user ?? initialUser } : { ...user, user };

  const wrapShowUserSelection =
    (showUserSelection === 'task' && taskUsersCanSelectUser.includes(user.email)) ||
    (showUserSelection === 'request' && requestUsersCanSelectUser.includes(user.email));

  return (
    <Form form={form} className="mb-m">
      <Row className="mb-m" gutter={10} wrap={false}>
        <Col style={{ flex: '1 1 120px' }}>
          <Form.Item
            initialValue="00:00"
            name="time"
            rules={[
              {
                validator(_, value) {
                  if (!value || value === '00:00') {
                    return Promise.reject(new Error('Time should be bigger than 00:00'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
            noStyle
          >
            <Input
              onFocus={(event) => {
                timeDigitsRef.current = takeTimeDigits(event.target.value);
                onTimeFocusChange?.(true);

                setTimeout(() => {
                  event.target.selectionStart = event.target.value.length;
                  event.target.selectionEnd = event.target.value.length;
                }, 0);
              }}
              onBlur={() => {
                onTimeFocusChange?.(false);

                const value = form.getFieldValue('time');
                const normalized = normalizeTime(value);

                if (normalized !== value) {
                  form.setFieldsValue({ time: normalized });
                }
              }}
              onKeyDown={(event) => {
                if (event.ctrlKey || event.metaKey || event.altKey) return;

                const input = event.currentTarget;
                const replacingAll =
                  input.selectionStart === 0 && input.selectionEnd === input.value.length && input.value.length > 0;
                const current = replacingAll ? '' : timeDigitsRef.current;

                if (/^[0-9]$/.test(event.key)) {
                  event.preventDefault();
                  applyTimeDigits(`${current}${event.key}`);
                  return;
                }

                if (event.key === 'Backspace' || event.key === 'Delete') {
                  event.preventDefault();
                  applyTimeDigits(replacingAll ? '' : current.slice(0, -1));
                }
              }}
              onChange={(event) => {
                // Fallback for input paths that never emit keydown (paste, autofill, mobile IMEs).
                applyTimeDigits(event.target.value.replace(/\D/g, ''));
              }}
              placeholder="00:00"
              suffix={
                <div className={styles.suffix}>
                  <ClockCircleOutlined />
                </div>
              }
            />
          </Form.Item>
        </Col>

        <Col flex={1}>
          <Form.Item name="date" noStyle>
            <DatePicker format="MM/DD/YYYY" defaultValue={moment(Date.now())} style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        {wrapShowUserSelection && (
          <Col xs={11}>
            <SelectSearch
              labelKey={(el) => el.user?.name}
              valueKey={(el) => el.user?.uuid}
              initialOptions={[wrappedUser]}
              initialValue={wrappedUser.user.uuid}
              render={(row: any) => {
                return (
                  <Row wrap={false} align="middle" gutter={[6, 0]}>
                    <Col>
                      <UserAvatar size="small" user={row} />
                    </Col>
                    <Col flex="auto">
                      <Text ellipsis>{row?.name}</Text>
                    </Col>
                  </Row>
                );
              }}
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
              style={{ width: '100%' }}
              searchKey="search_term"
              name="user_id"
              placeholder="Select user"
              api={apiRoutes.CONTACTS}
              defaultParams={{
                'sort[by]': 'name',
                'filters[type][]': ['regular_user', 'freelancer'],
                'filters[billing_contacts.status][]': 'active',
              }}
              noStyle
            />
          </Col>
        )}
      </Row>

      <Form.Item
        name="description"
        rules={
          requiredDescription
            ? [
                {
                  required: true,
                  whitespace: true,
                  message: 'Please input your Comment!',
                },
              ]
            : undefined
        }
        noStyle
      >
        <TextArea placeholder="Comment" rows={5} />
      </Form.Item>
    </Form>
  );
};

export default TimerForm;
